import { useState } from 'react';
import { ExternalLink, FileText, Link2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dropzone } from '@/components/ui/dropzone';
import { Input } from '@/components/ui/input';
import { openEquipmentFile } from '../../openEquipmentFile';
import { useDeleteEquipmentDocument, useLinkEquipmentDocument, useUploadEquipmentDocument } from '../../hooks/useEquipment';
import { DOCUMENT_KINDS } from '../../types';
import type { DocumentKind, EquipmentDocument } from '../../types';
import { Section, SelectField } from '../fields';

const KIND_OPTIONS = DOCUMENT_KINDS.map((kind) => ({ value: kind, label: kind[0].toUpperCase() + kind.slice(1) }));

export function EquipmentDocumentsTab({ modelId, documents, canWrite }: { modelId: string; documents: EquipmentDocument[]; canWrite: boolean }) {
  const upload = useUploadEquipmentDocument();
  const link = useLinkEquipmentDocument();
  const remove = useDeleteEquipmentDocument();
  const [kind, setKind] = useState<DocumentKind>('manual');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  const handleFiles = (files: File[]) => {
    for (const file of files) {
      upload.mutate({ id: modelId, file, kind, title: title.trim() || file.name.replace(/\.pdf$/i, '') });
    }
    setTitle('');
  };

  return (
    <div className="space-y-5">
      <Section label={`Documents (${documents.length})`}>
        {documents.length === 0 && <p className="text-sm text-muted-foreground">No manuals or datasheets yet.</p>}
        <ul className="divide-y rounded-md border">
          {documents.map((document) => (
            <li key={document.id} className="flex items-center gap-3 px-3 py-2">
              {document.externalUrl ? <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" /> : <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{document.title}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {document.kind}{document.pageHint ? ` · ${document.pageHint}` : ''}
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Open ${document.title}`}
                onClick={() => openEquipmentFile('documents', document.id)}>
                <ExternalLink className="h-4 w-4" />
              </Button>
              {canWrite && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" aria-label={`Remove ${document.title}`}
                  onClick={() => remove.mutate({ id: modelId, documentId: document.id })}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      </Section>

      {canWrite && (
        <Section label="Add a document">
          <div className="grid grid-cols-2 gap-3">
            <SelectField<DocumentKind> id="eq-doc-kind" label="Kind" value={kind} options={KIND_OPTIONS} onChange={(next) => next && setKind(next)} />
            <div className="space-y-1">
              <label htmlFor="eq-doc-title" className="text-xs font-medium">Title</label>
              <Input id="eq-doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Defaults to the file name" className="h-8 text-sm" />
            </div>
          </div>
          <Dropzone onFiles={handleFiles} accept={{ 'application/pdf': ['.pdf'] }} disabled={upload.isPending} label="Drop a PDF (up to 50 MB) or click to choose" />
          <form className="flex gap-2" onSubmit={(e) => {
            e.preventDefault();
            if (!url.trim()) return;
            link.mutate({ id: modelId, url: url.trim(), kind, title: title.trim() || 'Manual' }, { onSuccess: () => { setUrl(''); setTitle(''); } });
          }}>
            <Input aria-label="Document link" type="url" placeholder="https://manufacturer.example/manual.pdf" value={url}
              onChange={(e) => setUrl(e.target.value)} className="h-8 text-sm" />
            <Button type="submit" variant="outline" size="sm" disabled={link.isPending}>Link URL</Button>
          </form>
        </Section>
      )}
    </div>
  );
}
